import * as React from 'react'

import {
  AppointmentFormTypes,
  AppointmentUtils,
  Components,
  Containers,
} from '@grandvisionhq/appointments'
import { Button, Delayed } from '@grandvisionhq/common'
import { Text } from '@grandvisionhq/elements'
import {
  ComposedForm,
  DateOfBirth,
  FormControl,
  FormTypes,
  PhoneNumberInput,
  Util,
} from '@grandvisionhq/forms'
import { Types } from '@grandvisionhq/graphql'
import { useIntl } from '@grandvisionhq/state'

import { consents, optIns } from '../../configuration'
import { INTL_DEFAULTS } from '../../configuration/intl'
import { countriesOrder } from '../../configuration/phone-number-input'

const { ConsentsAndOptIns } = Components
const { SignInBox } = Containers

const BookAppointmentForm: AppointmentFormTypes.FormComponent = ({
  submitting = false,
  formAlert = null,
  loggedIn,
  onChange,
  disableSubmit = false,
  beforeSubmitSlot = null,
  ...rest
}) => {
  const { getLabel } = useIntl()

  const onChangeHandler = React.useCallback(
    (state: FormTypes.ComposedFormState) => {
      onChange?.(state)
    },
    [onChange]
  )

  return (
    <ComposedForm {...rest} className="book-appointment-form" onChange={onChangeHandler}>
      {!loggedIn && <SignInBox />}
      <ComposedForm.Part name="personalDetails">
        <FormControl
          {...FormControl.defaultProps}
          id="firstName"
          data-t="personal-details-first-name"
          label={getLabel(
            'appointments.component.bookAppointmentForm.label.personalDetails.firstName'
          )}
          validations={[Util.validations.isText, Util.validations.maxLength(30)]}
          errorMessage={getLabel(
            'appointments.component.bookAppointmentForm.error.personalDetails.firstName'
          )}
        />
        <FormControl
          {...FormControl.defaultProps}
          id="lastName"
          data-t="personal-details-last-name"
          label={getLabel(
            'appointments.component.bookAppointmentForm.label.personalDetails.lastName'
          )}
          validations={[Util.validations.isText, Util.validations.maxLength(255)]}
          errorMessage={getLabel(
            'appointments.component.bookAppointmentForm.error.personalDetails.lastName'
          )}
        />
        <FormControl
          {...FormControl.defaultProps}
          id="email"
          type="email"
          data-t="contact-details-email"
          label={getLabel('appointments.component.bookAppointmentForm.label.contactDetails.email')}
          validations={[Util.validations.isEmailAddress, Util.validations.maxLength(255)]}
          errorMessage={getLabel(
            'appointments.component.bookAppointmentForm.error.contactDetails.email'
          )}
          disabled={loggedIn}
        />
        <PhoneNumberInput
          {...FormControl.defaultProps}
          id="mobilePhone"
          data-t="contact-details-mobile-phone"
          label={getLabel(
            'appointments.component.bookAppointmentForm.label.contactDetails.mobilePhone'
          )}
          errorMessage={getLabel(
            'appointments.component.bookAppointmentForm.error.contactDetails.mobilePhone'
          )}
          required
          defaultCountry={INTL_DEFAULTS.country}
          localeForTranslations={INTL_DEFAULTS.locale}
          countriesOrder={countriesOrder}
        />
        <DateOfBirth {...FormControl.defaultProps} name="dateOfBirth" required={false} />
      </ComposedForm.Part>

      {!loggedIn && (
        <ComposedForm.Part name="consentsAndOptIns">
          <ConsentsAndOptIns
            className="book-appointment-form__spacing-top"
            errorMessage={getLabel('appointments.component.consentsAndOptIns.error')}
            consents={consents}
            optIns={optIns}
          />
        </ComposedForm.Part>
      )}

      {formAlert}
      {beforeSubmitSlot}

      <Button
        type="submit"
        caption={getLabel('appointments.component.bookAppointmentForm.submit')}
        isLoading={submitting}
        disabled={disableSubmit}
        className="book-appointment-form__spacing-top"
      />
      {submitting && (
        <Delayed delay={2500} scrollIntoView className="book-appointment-form__spacing-top">
          <Text
            color="text-secondary"
            align="center"
            className="book-appointment-form__spacing-top"
          >
            {getLabel('appointments.component.bookAppointmentForm.slowLoadingIndication')}
          </Text>
        </Delayed>
      )}
    </ComposedForm>
  )
}

BookAppointmentForm.fromDataToState = (data) => {
  const state: FormTypes.ComposedFormValueState = {}

  const personalDetails: Record<string, string | number> = {
    firstName: data.defaultBillingAddress?.firstName ?? data.personalDetails?.firstName ?? '',
    lastName: data.defaultBillingAddress?.lastName ?? data.personalDetails?.lastName ?? '',
    email: data.email,
    mobilePhone: data.personalDetails?.phone ?? '',
    dateOfBirth: data.personalDetails?.dateOfBirth,
  }

  return Util.mergeComposedFormValueState(state, {
    personalDetails: Util.fieldsToFormState(personalDetails),
  })
}

BookAppointmentForm.getFormGroups = () => [
  'personalDetails',
  'additionalDetails',
  'consentsAndOptIns',
]

BookAppointmentForm.fromStateToData = (state, { intl }, metadata) => {
  const data = Util.getValuesFromComposedState(state)
  const consentsAreDisplayed =
    data.consentsAndOptIns && 'termsConditionsAndPolicies' in data.consentsAndOptIns
  const optInsAreDisplayed = data.consentsAndOptIns && 'EMAIL_MARKETING' in data.consentsAndOptIns
  const termsAndConditionsConsent = consents.find(
    (consent) => consent.value === 'termsConditionsAndPolicies'
  )
  const optIns = termsAndConditionsConsent?.children ?? []

  const result: AppointmentFormTypes.FormStateData = {
    personalDetails: {
      firstName: data.personalDetails?.firstName ?? '',
      lastName: data.personalDetails?.lastName ?? '',
      email: data.personalDetails?.email ?? '',
      phone: data.personalDetails?.mobilePhone ?? '',
      dateOfBirth: Util.toStandardDateFormat(data.personalDetails?.dateOfBirth),
      billingAddress: {
        firstName: data.personalDetails?.firstName ?? '',
        lastName: data.personalDetails?.lastName ?? '',
      },
    },
    child: null,
    consents:
      consentsAreDisplayed && !metadata?.loggedIn
        ? [
            {
              consentName: 'terms-conditions',
              value: data.consentsAndOptIns?.termsConditionsAndPolicies === 'true',
              uiType: Types.UIType.CHECKBOX,
              literalText: intl.getFormattedLabel(
                AppointmentUtils.getLiteralText('termsConditionsAndPolicies', consents) ?? '',
                { parseHTML: false, parseLink: true }
              ),
            },
          ]
        : null,
    optins:
      optInsAreDisplayed && !metadata?.loggedIn
        ? [
            {
              id: 'EMAIL_MARKETING',
              value: data.consentsAndOptIns?.EMAIL_MARKETING === 'true',
              uiType: Types.UIType.CHECKBOX,
              literalText: intl.getFormattedLabel(
                AppointmentUtils.getLiteralText('EMAIL_MARKETING', optIns) ?? '',
                { parseHTML: false, parseLink: true }
              ),
            },
            {
              id: 'MOBILE_MARKETING',
              value: data.consentsAndOptIns?.MOBILE_MARKETING === 'true',
              uiType: Types.UIType.CHECKBOX,
              literalText: intl.getFormattedLabel(
                AppointmentUtils.getLiteralText('MOBILE_MARKETING', optIns) ?? '',
                { parseHTML: false, parseLink: true }
              ),
            },
          ]
        : null,
    customerComment: null,
    preferredChannelOfCommunication: null,
  }

  return result
}

export default BookAppointmentForm
